import React, { useState, useEffect, useCallback } from 'react';

import { Sun, Moon, Download, Upload, Printer } from 'lucide-react';

// Import advanced analytics
import AnalyticsTab from './components/AnalyticsTab';
import ReadmeTab from './components/ReadmeTab';
import ChartsTab from './components/ChartsTab';
import LoanRepaymentTab from './components/LoanRepaymentTab';
import TotalCostTab from './components/TotalCostTab';
import InvestmentFinancingTab from './components/InvestmentFinancingTab';
import ProfitLossTab from './components/ProfitLossTab';
import BalanceSheetTab from './components/BalanceSheetTab';
import EVATab from './components/EVATab';
import BESSTab from './components/BESSTab';
import { logger } from './utils/logger';

// Import UI components
import { Tab } from './components/ui';

// Import calculation services
import { calculateFinancials } from './services/financialCalculations';

// Import tab components
import SummaryTab from './components/SummaryTab';
import BasicDataTab from './components/BasicDataTab';
import SensitivityAnalysisTab from './components/SensitivityAnalysisTab';

// Utility functions are now imported by individual services as needed

// Import business logic services
import { printReport } from './services/printService';
import { findRequiredOwnFunds } from './services/goalSeekService';
import { exportParametersToCSV, handleFileInputChange, triggerFileImport } from './utils/fileUtils';
import { validateInputValue } from './utils/inputValidation';

// Import constants
import {
    DEFAULT_PROJECT_INPUTS,
    TABS,
    APP_METADATA,
    UI_LABELS,
    DEFAULT_UI_STATE,
    UI_THEME,
    FILE_CONFIG
} from './constants';



// --- Main App Component ---
export default function App() {
    const [activeTab, setActiveTab] = useState(DEFAULT_UI_STATE.ACTIVE_TAB);
    const [isLoadingGoalSeek, setIsLoadingGoalSeek] = useState(DEFAULT_UI_STATE.IS_LOADING_GOAL_SEEK);
    const [goalSeekMessage, setGoalSeekMessage] = useState(DEFAULT_UI_STATE.GOAL_SEEK_MESSAGE);
    const [isDarkMode, setIsDarkMode] = useState(DEFAULT_UI_STATE.DARK_MODE);
    const fileInputRef = React.useRef(null);

    const [inputs, setInputs] = useState(DEFAULT_PROJECT_INPUTS);

    const [outputs, setOutputs] = useState({});

    useEffect(() => {
        try {
            logger.info('Calculating financials', { inputs });
            const result = calculateFinancials(inputs);
            if (result && typeof result === 'object') {
                setOutputs(result);
                logger.info('Financials calculated successfully', { 
                    projectIRR: result.indicators?.projectIRR,
                    capitalIRR: result.indicators?.capitalIRR,
                    projectNPV: result.indicators?.projectNPV 
                });
            }
        } catch (error) {
            logger.error('Calculation error', { error: error.message, stack: error.stack });
            console.error('Calculation error:', error);
            setOutputs({}); // Safe fallback
        }
    }, [inputs]);

    // Expose calculateFinancials to window for use by Analytics tab
    useEffect(() => {
        window.calculateFinancials = calculateFinancials;
        return () => {
            delete window.calculateFinancials;
        };
    }, []);

    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [isDarkMode]);

    const handleInputChange = (id, value) => {
        const validation = validateInputValue(id, value);
        if (validation.valid) {
            setInputs(prev => ({ ...prev, [id]: validation.value }));
        }
    };

    const handleExport = () => {
        exportParametersToCSV(inputs);
    };

    const handleImportClick = () => {
        triggerFileImport(fileInputRef);
    };

    const handleImport = (event) => {
        handleFileInputChange(event, setInputs);
    };

    const handlePrint = async () => {
        await printReport(inputs, outputs, TABS);
    };

    const runGoalSeek = useCallback(() => {
        findRequiredOwnFunds(
            inputs, 
            inputs.goalSeekTargetIRR, 
            setIsLoadingGoalSeek, 
            setGoalSeekMessage, 
            setInputs
        );
    }, [inputs]);

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'Basic Data': return <BasicDataTab inputs={inputs} outputs={outputs} onChange={handleInputChange} onGoalSeek={runGoalSeek} isLoading={isLoadingGoalSeek} goalSeekMessage={goalSeekMessage} />;
            case 'Summary': return <SummaryTab outputs={outputs} inputs={inputs} />;
            case 'Investment & Financing': return <InvestmentFinancingTab outputs={outputs} />;
            case 'BESS Analysis': return <BESSTab outputs={outputs} inputs={inputs} />;
            case 'Loan Repayment': return <LoanRepaymentTab outputs={outputs} />;
            case 'Total Cost': return <TotalCostTab outputs={outputs} />;
            case 'Profit & Loss': return <ProfitLossTab outputs={outputs} />;
            case 'Balance Sheet': return <BalanceSheetTab outputs={outputs} />;
            case 'EVA': return <EVATab outputs={outputs} />;
            case 'Charts': return <ChartsTab outputs={outputs} inputs={inputs} isDarkMode={isDarkMode} />;
            case 'Sensitivity Analysis': return <SensitivityAnalysisTab outputs={outputs} inputs={inputs} />;
            case 'Analytics': return <AnalyticsTab inputs={inputs} outputs={outputs} setInputs={setInputs} />;
            case 'README': return <ReadmeTab outputs={outputs} inputs={inputs} isDarkMode={isDarkMode} />;
            default: return <SummaryTab outputs={outputs} inputs={inputs} />;
        }
    };

    return (
        <div className={UI_THEME.CONTAINER_CLASSES}>
            <style>
                {`
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        .printable-area, .printable-area * {
                            visibility: visible;
                        }
                        .printable-area {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                        }
                        .no-print {
                            display: none;
                        }
                        
                        /* Summary Tab Print Optimization */
                        .summary-print-container {
                            font-size: 13px !important;
                            line-height: 1.4 !important;
                            margin: 0 !important;
                            padding: 12px !important;
                        }
                        .summary-print-container h2 {
                            font-size: 20px !important;
                            margin-bottom: 12px !important;
                            font-weight: bold !important;
                        }
                        .summary-print-container h3 {
                            font-size: 16px !important;
                            margin-bottom: 8px !important;
                            font-weight: 600 !important;
                        }
                        .summary-print-container h4 {
                            font-size: 13px !important;
                            margin-bottom: 6px !important;
                            font-weight: 600 !important;
                        }
                        .summary-print-container .grid {
                            gap: 12px !important;
                        }
                        .summary-print-container .p-4 {
                            padding: 10px !important;
                        }
                        .summary-print-container .p-6 {
                            padding: 12px !important;
                        }
                        .summary-print-container .mb-3 {
                            margin-bottom: 6px !important;
                        }
                        .summary-print-container .mb-4 {
                            margin-bottom: 8px !important;
                        }
                        .summary-print-container .mb-6 {
                            margin-bottom: 10px !important;
                        }
                        .summary-print-container .mt-8 {
                            margin-top: 16px !important;
                        }
                        .summary-print-container .space-y-4 > * + * {
                            margin-top: 8px !important;
                        }
                        .summary-print-container .text-lg {
                            font-size: 15px !important;
                        }
                        .summary-print-container .text-xl {
                            font-size: 16px !important;
                        }
                        .summary-print-container .text-2xl {
                            font-size: 20px !important;
                        }
                        .summary-print-container .text-sm {
                            font-size: 11px !important;
                        }
                        .summary-print-container .border {
                            border-width: 1px !important;
                        }
                        .summary-print-container .rounded-lg {
                            border-radius: 4px !important;
                        }
                        .summary-print-container .w-5 {
                            width: 16px !important;
                            height: 16px !important;
                        }
                        .summary-print-container .kpi-section {
                            page-break-before: always !important;
                        }
                        
                        /* Preserve Grid Layouts for Print */
                        .summary-print-container .grid.md\\:grid-cols-2 {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr !important;
                        }
                        .summary-print-container .grid.grid-cols-1.md\\:grid-cols-3 {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr 1fr !important;
                        }
                        .summary-print-container .grid.md\\:grid-cols-2.gap-x-6 {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr !important;
                        }
                        .summary-print-container .md\\:col-span-2 {
                            grid-column: span 2 !important;
                        }
                    }
                `}
            </style>
            <header className={UI_THEME.HEADER_CLASSES}>
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">{APP_METADATA.TITLE}</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">{APP_METADATA.SUBTITLE}</p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2">
                    <img 
                        src="/logo.webp" 
                        alt="Company Logo" 
                        className="h-12 w-auto object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleImport} style={{ display: 'none' }} accept={FILE_CONFIG.ACCEPTED_TYPES} />
                    <button onClick={handleImportClick} title={UI_LABELS.IMPORT_TOOLTIP} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        <Upload size={UI_THEME.ICON_SIZE} />
                    </button>
                    <button onClick={handleExport} title={UI_LABELS.EXPORT_TOOLTIP} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        <Download size={UI_THEME.ICON_SIZE} />
                    </button>
                     <button onClick={handlePrint} title={UI_LABELS.PRINT_TOOLTIP} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        <Printer size={UI_THEME.ICON_SIZE} />
                    </button>
                    <button onClick={() => setIsDarkMode(!isDarkMode)} title={UI_LABELS.DARK_MODE_TOOLTIP} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        {isDarkMode ? <Sun size={UI_THEME.ICON_SIZE} /> : <Moon size={UI_THEME.ICON_SIZE} />}
                    </button>
                </div>
            </header>
            <div className="no-print flex flex-wrap border-b border-gray-200 dark:border-gray-700">{TABS.map(tab => (<Tab key={tab} label={tab} isActive={activeTab === tab} onClick={() => setActiveTab(tab)} />))}</div>
            <main className={UI_THEME.MAIN_CLASSES}>
                {renderActiveTab()}
            </main>
            <footer className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400 no-print"><p>{APP_METADATA.FOOTER_TEXT}</p></footer>
        </div>
    );
}




